import React, { FC } from 'react'
import classNames from 'classnames'
import styles from './Footer.module.scss'
import { COPYRIGHTS_LINKS, TOP_CATEGORIES } from './FooterData'
import PreferencesFooter from './Preferences/Preferences'
import SALink from 'components/SALink'
import FooterLinks from './Links/Links'
import MobileFooterLinks from './Links/MobileLinks'
import MailSection from './MailSection/MailSection'

type FooterProps = {
  isStudio?: boolean
}

const Footer: FC<FooterProps> = ({ isStudio }) => {
  return (
    <footer
      data-section='footer'
      className={classNames(styles.footerComponent, {
        [styles.footerComponentStudio]: isStudio,
      })}
    >
      {!isStudio && (
        <>
          <div className={styles.topCategoriesWrapper}>
            {TOP_CATEGORIES.map(({ title, categories }) => (
              <div className={styles.topCategoriesContainer} key={title}>
                <span>{title}</span>
                <div className={styles.topCategoriesLinks} data-type='top-categories-links'>
                  {categories.map(({ title: categoryTitle, url }) => (
                    <SALink key={categoryTitle} href={url} data-gtm-event='footer'>
                      {categoryTitle}
                    </SALink>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <hr className='hrDivider' />
        </>
      )}

      <div
        className={classNames(styles.footerContainer, {
          [styles.footerContainerStudio]: isStudio,
        })}
      >
        <div
          className={classNames(styles.topSectionWrapper, {
            [styles.topSectionWrapperStudio]: isStudio,
          })}
        >
          <div
            className={classNames(styles.topSectionContainer, {
              [styles.topSectionContainerStudio]: isStudio,
            })}
          >
            <MailSection isStudio={isStudio} />
            <div className={styles.topSectionColumnSeparator} />
            <FooterLinks isStudio={isStudio} />
          </div>

          <hr className='hrDivider' />
        </div>

        <div
          className={classNames(styles.mobileFooterLinks, {
            [styles.mobileFooterLinksStudio]: isStudio,
          })}
        >
          <MobileFooterLinks isStudio={isStudio} />
        </div>

        <div
          className={classNames(styles.copyrightWrapper, {
            [styles.copyrightWrapperStudio]: isStudio,
          })}
        >
          <div
            className={classNames(styles.copyrightBox, {
              [styles.copyrightBoxStudio]: isStudio,
            })}
          >
            <div
              className={classNames(styles.copyrightItemsContainer, {
                [styles.copyrightItemsContainerStudio]: isStudio,
              })}
            >
              <div
                className={classNames(styles.copyrightLinksBox, {
                  [styles.copyrightLinksBoxStudio]: isStudio,
                })}
                data-type='copyright-link-box'
              >
                {COPYRIGHTS_LINKS.map(({ title, link }) => {
                  const isUserPreferences = link === '#userPreferences'
                  // Opens OneTrust modal
                  return isUserPreferences ? (
                    <span
                      key={title}
                      className={classNames('defaultTypographyTiny', 'ot-sdk-show-settings', {
                        [styles.studioText]: isStudio,
                      })}
                    >
                      {title}
                    </span>
                  ) : (
                    <SALink
                      key={title}
                      href={link}
                      data-gtm-event='footer'
                      className={classNames('defaultTypographyTiny', {
                        [styles.studioLink]: isStudio,
                      })}
                    >
                      {title}
                    </SALink>
                  )
                })}
              </div>
            </div>
          </div>
          <PreferencesFooter isStudio={isStudio} />

          <div
            className={classNames(styles.copyrightRecaptcha, {
              [styles.copyrightRecaptchaStudio]: isStudio,
            })}
          >
            <span className='defaultTypographyTiny'>
              © 2010-{new Date().getFullYear()} Saatchi Art. All Rights Reserved.
            </span>
            <p className='defaultTypographyTiny'>
              {`This site is protected by reCAPTCHA and the Google `}
              <SALink
                className={classNames(styles.copyrightRecaptchaLink, {
                  [styles.copyrightRecaptchaLinkStudio]: isStudio,
                })}
                target='_blank'
                data-gtm-event='footer'
                rel='noopener noreferrer'
                href='https://policies.google.com/privacy'
                title='Privacy Policy'
              >
                Privacy Policy
              </SALink>
              {` and `}
              <SALink
                className={classNames(styles.copyrightRecaptchaLink, {
                  [styles.copyrightRecaptchaLinkStudio]: isStudio,
                })}
                target='_blank'
                data-gtm-event='footer'
                rel='noopener noreferrer'
                href='https://policies.google.com/terms'
                title='Terms of Service'
              >
                Terms of Service
              </SALink>
              {` apply.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
